import { isSyntheticOpener } from './coach/opener-intent';
import { toolParts } from './tool-parts';
import { TOOLS } from './tools/registry';
import type { PlannerUIMessage } from './types';

/**
 * True when the message has a pending client-actions batch with no live stream: its turn
 * died before the result was recorded, so nothing can resolve it now.
 */
const hasInterruptedBatch = (
  message: PlannerUIMessage,
  isLive: (toolCallId: string) => boolean,
): boolean =>
  toolParts(message, TOOLS.executeClientActions).some(
    (part) => part.state === 'input-available' && !isLive(part.toolCallId),
  );

/**
 * Which messages show the "may not have been applied" status. The call is on the assistant
 * message, but the status belongs to the couple's request, so it marks the nearest user
 * message before it. The assistant message keeps it only when there is no user message.
 * The synthetic opener is skipped, so the status is always on a visible bubble.
 *
 * Only client-actions batches get one, as only their writes may not have been applied.
 */
export const deriveInterruptedRequestIds = (
  messages: PlannerUIMessage[],
  isLive: (toolCallId: string) => boolean,
): Set<string> => {
  const visible = messages.filter((message) => !isSyntheticOpener(message));
  const ids = new Set<string>();
  visible.forEach((message, index) => {
    if (message.role !== 'assistant') return;
    if (!hasInterruptedBatch(message, isLive)) return;
    let request: PlannerUIMessage | undefined;
    for (let i = index - 1; i >= 0 && !request; i -= 1) {
      if (visible[i].role === 'user') request = visible[i];
    }
    ids.add((request ?? message).id);
  });
  return ids;
};
